import React from 'react';
import { Link } from 'react-router-dom';
import { Edit3 } from 'lucide-react';
import { formatReadable, relativeTime } from './contractDetailUtils';

// Group heading with a hover-revealed pencil deep-linking into the owning
// edit-form section (.rail-group-edit shows on card hover / focus / touch).
function GroupTitle({ canWrite, children, editHref, editTitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div className="detail-section-title" style={{ flex: 1, marginBottom: 0 }}>{children}</div>
      {canWrite && editHref && (
        <Link to={editHref} className="icon-btn xs rail-group-edit" title={editTitle} aria-label={editTitle}>
          <Edit3 className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

// Rail = exactly two cards: "About this contract" (Parties + Classification
// kv groups) and "Recent activity" (latest 3 + a link to the Timeline tab).
// Dates, money, renewal, and law deliberately do NOT appear here — the fact
// strip above the tabs is their only home on this page.
export default function ContractDetailRail({
  additionalParties,
  canWriteContracts,
  contract,
  contractId,
  counterpartyContactEmail,
  counterpartyContactName,
  counterpartyContactPhone,
  counterpartyContactRole,
  setTab,
  timeline,
}) {
  const c = contract;

  const contactLine = [counterpartyContactName, counterpartyContactRole].filter(Boolean).join(' · ');

  const partyRows = [];
  if (c.internalOwner) partyRows.push(['Owner', c.internalOwner]);
  if (c.counterparty) partyRows.push(['Counterparty', c.counterparty]);
  if (Array.isArray(c.counterpartyAliases) && c.counterpartyAliases.length > 0) {
    partyRows.push(['Also known as', c.counterpartyAliases.join(', ')]);
  }
  if (c.counterpartyGroup) partyRows.push(['Group', c.counterpartyGroup]);
  if (additionalParties.length > 0) {
    partyRows.push(['Other parties', (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {additionalParties.map((party, index) => (
          <div key={`${party.name}-${index}`}>
            <span>{party.name}</span>
            {party.role && (
              <span className="muted" style={{ fontSize: 12 }}> — {party.role}</span>
            )}
          </div>
        ))}
      </div>
    )]);
  }
  if (contactLine) partyRows.push(['Contact', contactLine]);
  if (counterpartyContactEmail) {
    partyRows.push(['Email', (
      <span className="mono" style={{ fontSize: 12 }}>
        <a href={`mailto:${counterpartyContactEmail}`}>{counterpartyContactEmail}</a>
      </span>
    )]);
  }
  if (counterpartyContactPhone) {
    partyRows.push(['Phone', (
      <span className="mono" style={{ fontSize: 12 }}>
        <a href={`tel:${counterpartyContactPhone}`}>{counterpartyContactPhone}</a>
      </span>
    )]);
  }
  if (!counterpartyContactName && !counterpartyContactEmail && !counterpartyContactPhone && c.counterpartyContact) {
    partyRows.push(['Contact note', <span className="mono" style={{ fontSize: 12 }}>{c.counterpartyContact}</span>]);
  }

  const classificationRows = [];
  if (c.contractType) {
    classificationRows.push(['Type', c.contractType === 'Other' && c.contractTypeOther ? c.contractTypeOther : c.contractType]);
  }
  if (c.category) classificationRows.push(['Category', c.category]);
  if (c.lightsparkEntity) classificationRows.push(['Entity', c.lightsparkEntity]);
  if (c.confidentialityStructure) {
    classificationRows.push(['Confidentiality', (
      <span>
        {formatReadable(c.confidentialityStructure)}
        {c.confidentialityStructure === 'one_way' && c.confidentialityDisclosingParty && (
          <span className="muted" style={{ fontSize: 12 }}> · {c.confidentialityDisclosingParty} discloses</span>
        )}
      </span>
    )]);
  }

  const renderKvRows = (rows) => (
    <dl className="kv-list">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt className="muted">{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );

  return (
    <>
        {/* Right rail */}
        <aside className="detail-rail">
          {(partyRows.length > 0 || classificationRows.length > 0) && (
            <div className="card">
              <div className="card-header compact">
                <span className="card-title">About this contract</span>
              </div>
              <div className="card-body" style={{ paddingTop: 16 }}>
                {partyRows.length > 0 && (
                  <div>
                    <GroupTitle
                      canWrite={canWriteContracts}
                      editHref={`/contracts/${contractId}/edit#sec-counterparty`}
                      editTitle="Edit parties"
                    >
                      Parties
                    </GroupTitle>
                    {renderKvRows(partyRows)}
                  </div>
                )}
                {classificationRows.length > 0 && (
                  <div style={{ marginTop: partyRows.length > 0 ? 20 : 0 }}>
                    <GroupTitle
                      canWrite={canWriteContracts}
                      editHref={`/contracts/${contractId}/edit#sec-basic`}
                      editTitle="Edit classification"
                    >
                      Classification
                    </GroupTitle>
                    {renderKvRows(classificationRows)}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body" style={{ paddingTop: 20 }}>
              <div className="detail-section-title">Recent activity</div>
              {timeline.length === 0 ? (
                <div className="muted" style={{ fontSize: 12 }}>No activity yet.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {timeline.slice(0, 3).map((e, i) => (
                      <div key={i} style={{ fontSize: 12, display: 'grid', gridTemplateColumns: '6px 1fr', gap: 10, alignItems: 'flex-start' }}>
                        <span style={{
                          marginTop: 5, width: 6, height: 6, borderRadius: 99,
                          background: i === 0 ? 'var(--color-green-500)' : 'var(--color-gray-300)',
                        }} />
                        <div>
                          <div>
                            <strong style={{ fontWeight: 500 }}>{e.who}</strong>{' '}
                            <span className="muted">{e.text}</span>
                          </div>
                          <div className="muted mono" style={{ fontSize: 10, marginTop: 2 }}>{relativeTime(e.when)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="btn ghost xs" style={{ marginTop: 12 }} onClick={() => setTab('timeline')}>
                    View full timeline →
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>
    </>
  );
}
